// Shared store for Lists, Calendar, the Dashboard tasks card and the sidebar
// badge. One copy of every visible list and item, kept fresh by short polling
// (see api/tasks.ts?r=sync) and edited optimistically.
//
// Three rules keep what's on screen honest while polls and writes overlap:
//   1. A row is only replaced by a copy with an equal or higher `rev`.
//   2. Rows with a write in flight are skipped by polls — the write's own
//      response is the authoritative copy and arrives right after.
//   3. Deleted rows stay in the store as tombstones (filtered out of views),
//      so an older copy re-sent by the poll's overlap window can't revive them.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ComposerPreset, TaskItem, TaskList, TaskNotification, TaskUser } from '../types/tasks';
import { MemberRow, RequestInput, taskApi, taskErrorMessage } from '../utils/taskApi';
import { descendantIds, recomputeProgress } from '../utils/taskTree';
import { businessToday } from '../utils/taskDates';
import { costTree } from '../utils/projectValue';

// Poll cadence. Fast where people watch lists change; slow elsewhere so the
// sidebar badge still updates; paused entirely while the tab is hidden.
const FAST_POLL_MS = 4000;
const SLOW_POLL_MS = 30000;
const FAST_ROUTES = ['/lists', '/calendar', '/projects'];

interface State {
  ready: boolean;
  error: string | null;
  today: string;
  weeklyListId: number | null;
  /** Sees the whole MPP week list (false: only the lines assigned to me on it). */
  weeklyViewer: boolean;
  rev: number;
  users: TaskUser[];
  lists: Map<number, TaskList>;
  items: Map<number, TaskItem>;
  members: Map<number, string[]>;
  notifications: TaskNotification[];
  /** Lists I've pinned, in pin order. */
  pinned: number[];
}

type Action =
  | { type: 'LOAD'; payload: Awaited<ReturnType<typeof taskApi.bootstrap>> }
  | { type: 'ERROR'; error: string }
  | { type: 'MERGE'; lists?: TaskList[]; items?: TaskItem[]; members?: MemberRow[]; memberListIds?: number[]; skip?: Set<number>; rev?: number; today?: string }
  | { type: 'VISIBLE'; ids: number[] }
  | { type: 'LOCAL_ITEMS'; items: TaskItem[] }
  | { type: 'DROP_ITEM'; id: number }
  | { type: 'DROP_ITEMS'; ids: number[] }
  | { type: 'WEEKLY_VIEWER'; value: boolean }
  | { type: 'LOCAL_LIST'; list: TaskList }
  | { type: 'NOTIFICATIONS'; notifications: TaskNotification[] }
  | { type: 'PINNED'; pinned: number[] };

const initialState: State = {
  ready: false,
  error: null,
  today: businessToday(),
  weeklyListId: null,
  weeklyViewer: true,
  rev: 0,
  users: [],
  lists: new Map(),
  items: new Map(),
  members: new Map(),
  notifications: [],
  pinned: [],
};

function groupMembers(rows: MemberRow[], into: Map<number, string[]>, listIds: number[]) {
  const next = new Map(into);
  listIds.forEach((id) => next.set(id, []));
  rows.forEach((row) => next.set(row.list_id, [...(next.get(row.list_id) || []), row.email]));
  return next;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'LOAD': {
      const p = action.payload;
      return {
        ready: true,
        error: null,
        today: p.today,
        weeklyListId: p.weekly_list_id,
        weeklyViewer: p.weekly_viewer !== false,
        rev: p.rev,
        users: p.users,
        lists: new Map(p.lists.map((l) => [l.id, l])),
        items: new Map(p.items.map((i) => [i.id, i])),
        members: groupMembers(p.members, new Map(), p.lists.map((l) => l.id)),
        notifications: p.notifications,
        pinned: p.pinned_list_ids || [],
      };
    }
    case 'ERROR':
      return { ...state, error: action.error };
    case 'MERGE': {
      const lists = new Map(state.lists);
      for (const list of action.lists || []) {
        const current = lists.get(list.id);
        if (current && current.rev > list.rev) continue;
        lists.set(list.id, list);
      }
      const items = new Map(state.items);
      for (const item of action.items || []) {
        if (action.skip?.has(item.id)) continue;
        const current = items.get(item.id);
        if (current && current.rev > item.rev) continue;
        items.set(item.id, current?.ckey ? { ...item, ckey: current.ckey } : item);
      }
      const members = action.members
        ? groupMembers(action.members, state.members, action.memberListIds || [])
        : state.members;
      return {
        ...state,
        lists,
        items,
        members,
        rev: Math.max(state.rev, action.rev ?? 0),
        today: action.today || state.today,
      };
    }
    case 'VISIBLE': {
      // Lists no longer visible (unshared, archived, deleted) leave the store.
      const keep = new Set(action.ids);
      const lists = new Map(Array.from(state.lists).filter(([id]) => keep.has(id)));
      const items = new Map(Array.from(state.items).filter(([, item]) => item.id < 0 || keep.has(item.list_id)));
      return { ...state, lists, items };
    }
    case 'LOCAL_ITEMS': {
      const items = new Map(state.items);
      action.items.forEach((item) => items.set(item.id, item));
      return { ...state, items };
    }
    case 'DROP_ITEM': {
      const items = new Map(state.items);
      items.delete(action.id);
      return { ...state, items };
    }
    case 'DROP_ITEMS': {
      if (!action.ids.some((id) => state.items.has(id))) return state;
      const items = new Map(state.items);
      action.ids.forEach((id) => items.delete(id));
      return { ...state, items };
    }
    case 'WEEKLY_VIEWER':
      return action.value === state.weeklyViewer ? state : { ...state, weeklyViewer: action.value };
    case 'LOCAL_LIST': {
      const lists = new Map(state.lists);
      lists.set(action.list.id, action.list);
      return { ...state, lists };
    }
    case 'NOTIFICATIONS':
      return { ...state, notifications: action.notifications };
    case 'PINNED':
      return { ...state, pinned: action.pinned };
    default:
      return state;
  }
}

export interface NewItemInput {
  list_id: number;
  text: string;
  parent_id?: number | null;
  sort_order?: number;
  assignees?: string[];
  note?: string | null;
  due_date?: string | null;
  link_type?: TaskItem['link_type'];
  link_ref?: string | null;
  link_label?: string | null;
  /** Project topics: a material added straight onto a shopping list. */
  node_kind?: TaskItem['node_kind'];
  qty?: number | null;
  cost?: number | null;
  vendor?: string | null;
}

interface TasksContextValue {
  ready: boolean;
  error: string | null;
  today: string;
  me: string;
  rev: number;
  weeklyListId: number | null;
  weeklyViewer: boolean;
  users: TaskUser[];
  lists: TaskList[];
  items: TaskItem[];
  itemsForList: (listId: number) => TaskItem[];
  /** Each item's cost including everything under it — materials count toward their task (utils/projectValue). */
  costs: Map<number, number>;
  /**
   * I can add to / edit this list (server: editableListSql). False for lists I
   * can only read because a task from them is on the week list.
   */
  canEditList: (list: TaskList) => boolean;
  members: Map<number, string[]>;
  notifications: TaskNotification[];
  refresh: () => Promise<void>;
  createList: (input: { title: string; color?: string | null; members?: string[]; items?: { text: string; depth: number }[] }) => Promise<TaskList | null>;
  updateList: (id: number, patch: { title?: string; color?: string | null; archived?: boolean; project_start?: string | null }) => Promise<void>;
  setMembers: (id: number, emails: string[]) => Promise<void>;
  deleteList: (id: number) => Promise<void>;
  /** A new, empty project (a list of kind 'project'). */
  createProject: (input: { title: string; color?: string | null }) => Promise<TaskList | null>;
  /** "Show on Lists": a Lists card for a project branch (null = the whole project), or its shopping list. Returns the card. */
  showOnLists: (projectId: number, rootItemId: number | null, filter?: 'branch' | 'materials') => Promise<TaskList | null>;
  /** Sends a warehouse Custom Request. Throws with a message for people on failure. */
  createRequest: (input: RequestInput) => Promise<TaskList>;
  createItem: (input: NewItemInput) => { tempId: number; key: string; done: Promise<TaskItem | null> };
  updateItem: (id: number, patch: Partial<TaskItem>) => Promise<void>;
  deleteItem: (id: number) => Promise<void>;
  moveItems: (listId: number, moves: { id: number; parent_id: number | null; depth: number; sort_order: number }[]) => Promise<void>;
  uploadImage: (listId: number, payload: { filename: string; contentType: string; data: string }) => Promise<void>;
  deleteImage: (listId: number, url: string) => Promise<void>;
  markSeen: (ids: number[] | 'all') => Promise<void>;
  pinned: number[];
  setPinned: (listId: number, pinned: boolean) => Promise<void>;
  linkToWeek: (itemId: number) => Promise<void>;
  unlinkFromWeek: (itemId: number) => Promise<void>;
  composer: ComposerPreset | null;
  openComposer: (preset?: ComposerPreset) => void;
  closeComposer: () => void;
  /** The list shown in the list dialog (opened from a linked week-list line), and the line to pulse. */
  listDialog: { listId: number; itemId: number | null } | null;
  openListDialog: (listId: number, itemId?: number | null) => void;
  closeListDialog: () => void;
  lastError: string | null;
  clearError: () => void;
}

const TasksContext = createContext<TasksContextValue | undefined>(undefined);

export const TasksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, permissions } = useAuth();
  const isAdmin = permissions?.user_type === 'Admin';
  const me = user?.email || '';
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const [composer, setComposer] = useState<ComposerPreset | null>(null);
  const [listDialog, setListDialog] = useState<{ listId: number; itemId: number | null } | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  // Writes in flight, by item id (rule 2 above).
  const pendingRef = useRef(new Map<number, number>());
  const hold = (ids: number[]) => ids.forEach((id) => pendingRef.current.set(id, (pendingRef.current.get(id) || 0) + 1));
  const release = (ids: number[]) =>
    ids.forEach((id) => {
      const n = (pendingRef.current.get(id) || 1) - 1;
      if (n <= 0) pendingRef.current.delete(id);
      else pendingRef.current.set(id, n);
    });

  // Temp ids for items created optimistically, and edits made to them before
  // the server has answered.
  const tempSeq = useRef(0);
  const tempIds = useRef(new Map<number, Promise<number | null>>());
  const tempPatches = useRef(new Map<number, Partial<TaskItem>>());

  const fail = useCallback((error: any, fallback: string) => {
    console.error(fallback, error);
    setLastError(taskErrorMessage(error, fallback));
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      dispatch({ type: 'LOAD', payload: await taskApi.bootstrap() });
    } catch (error: any) {
      dispatch({ type: 'ERROR', error: taskErrorMessage(error, 'Could not load tasks') });
    }
  }, []);

  const syncing = useRef(false);
  const syncNow = useCallback(async () => {
    const s = stateRef.current;
    if (!s.ready || syncing.current) return;
    syncing.current = true;
    try {
      const delta = await taskApi.sync(s.rev);
      dispatch({
        type: 'MERGE',
        lists: delta.lists,
        items: delta.items,
        members: delta.members,
        memberListIds: delta.member_list_ids,
        skip: new Set(pendingRef.current.keys()),
        rev: delta.rev,
        today: delta.today,
      });
      if (delta.hidden_item_ids?.length) dispatch({ type: 'DROP_ITEMS', ids: delta.hidden_item_ids });
      if (delta.weekly_viewer !== undefined) dispatch({ type: 'WEEKLY_VIEWER', value: delta.weekly_viewer });
      dispatch({ type: 'NOTIFICATIONS', notifications: delta.notifications });
      if (delta.pinned_list_ids) dispatch({ type: 'PINNED', pinned: delta.pinned_list_ids });
      const known = stateRef.current.lists;
      if (delta.visible_list_ids.some((id) => !known.has(id))) {
        // Newly visible list (shared with me, next week's list): its older
        // items aren't in the delta, so take a full snapshot instead.
        await bootstrap();
      } else {
        dispatch({ type: 'VISIBLE', ids: delta.visible_list_ids });
      }
    } catch (error) {
      console.warn('Task sync failed', error);
    } finally {
      syncing.current = false;
    }
  }, [bootstrap]);

  useEffect(() => {
    if (me) bootstrap();
  }, [me, bootstrap]);

  const fast = location.pathname === '/' || FAST_ROUTES.some((r) => location.pathname.startsWith(r));
  const intervalRef = useRef(SLOW_POLL_MS);
  intervalRef.current = fast ? FAST_POLL_MS : SLOW_POLL_MS;

  useEffect(() => {
    if (!state.ready) return;
    let timer: ReturnType<typeof setTimeout>;
    let stopped = false;
    const schedule = () => {
      if (!stopped) timer = setTimeout(tick, intervalRef.current);
    };
    const tick = async () => {
      if (document.visibilityState === 'visible') await syncNow();
      schedule();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncNow();
    };
    schedule();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [state.ready, syncNow]);

  // Entering Lists / Calendar shouldn't wait out a slow-poll interval.
  useEffect(() => {
    if (fast) syncNow();
  }, [fast, syncNow]);

  const mergeResult = useCallback((result: { items?: TaskItem[]; members?: MemberRow[] }) => {
    const memberListIds = result.members ? Array.from(new Set(result.members.map((m) => m.list_id))) : [];
    dispatch({ type: 'MERGE', items: result.items, members: result.members, memberListIds });
  }, []);

  /** Resolves a temp id to the real one once its create has landed. */
  const realId = useCallback(async (id: number) => (id < 0 ? (await tempIds.current.get(id)) ?? null : id), []);

  // ── Lists ──────────────────────────────────────────────────────────────────

  const createList = useCallback<TasksContextValue['createList']>(async (input) => {
    try {
      const result = await taskApi.createList(input);
      dispatch({ type: 'MERGE', lists: [result.list], items: result.items, members: result.members, memberListIds: [result.list.id] });
      return result.list;
    } catch (error) {
      fail(error, 'Could not create the list');
      return null;
    }
  }, [fail]);

  const updateList = useCallback<TasksContextValue['updateList']>(async (id, patch) => {
    const current = stateRef.current.lists.get(id);
    if (current) dispatch({ type: 'LOCAL_LIST', list: { ...current, ...patch } as TaskList });
    try {
      const { list, items } = await taskApi.updateList({ id, ...patch });
      if (patch.archived === false) {
        // Restored from the archive: its items aren't in the store yet.
        await bootstrap();
        return;
      }
      // A project's new start date re-plans it; the moved rows come back too.
      dispatch({ type: 'MERGE', lists: [list], items });
      if (list.archived) dispatch({ type: 'VISIBLE', ids: Array.from(stateRef.current.lists.keys()).filter((l) => l !== id) });
    } catch (error) {
      if (current) dispatch({ type: 'LOCAL_LIST', list: current });
      fail(error, 'Could not update the list');
    }
  }, [fail, bootstrap]);

  const setMembers = useCallback(async (id: number, emails: string[]) => {
    try {
      const result = await taskApi.setMembers(id, emails);
      dispatch({ type: 'MERGE', members: result.members, memberListIds: [id] });
    } catch (error) {
      fail(error, 'Could not update sharing');
    }
  }, [fail]);

  const deleteList = useCallback(async (id: number) => {
    const current = stateRef.current.lists.get(id);
    if (current) dispatch({ type: 'LOCAL_LIST', list: { ...current, deleted_at: new Date().toISOString() } });
    try {
      await taskApi.deleteList(id);
      dispatch({ type: 'VISIBLE', ids: Array.from(stateRef.current.lists.keys()).filter((l) => l !== id) });
    } catch (error) {
      if (current) dispatch({ type: 'LOCAL_LIST', list: current });
      fail(error, 'Could not delete the list');
    }
  }, [fail]);

  const createProject = useCallback<TasksContextValue['createProject']>(async (input) => {
    try {
      const result = await taskApi.createProject(input);
      dispatch({ type: 'MERGE', lists: [result.list], members: result.members, memberListIds: [result.list.id] });
      return result.list;
    } catch (error) {
      fail(error, 'Could not create the project');
      return null;
    }
  }, [fail]);

  const showOnLists = useCallback<TasksContextValue['showOnLists']>(async (projectId, rootItemId, filter = 'branch') => {
    try {
      const target = rootItemId !== null ? await realId(rootItemId) : null;
      const result = await taskApi.createView(projectId, target, filter);
      dispatch({ type: 'MERGE', lists: [result.list], members: result.members, memberListIds: [result.list.id] });
      return result.list;
    } catch (error) {
      fail(error, 'Could not add it to your Lists');
      return null;
    }
  }, [fail, realId]);

  const createRequest = useCallback(async (input: RequestInput) => {
    const result = await taskApi.createRequest(input);
    dispatch({ type: 'MERGE', lists: [result.list], items: result.items, members: result.members, memberListIds: [result.list.id] });
    return result.list;
  }, []);

  // ── Items ──────────────────────────────────────────────────────────────────

  const createItem = useCallback<TasksContextValue['createItem']>((input) => {
    const tempId = -(++tempSeq.current);
    const ckey = `tmp-${tempSeq.current}`;
    const s = stateRef.current;
    const parent = input.parent_id ? s.items.get(input.parent_id) : undefined;
    const siblings = Array.from(s.items.values()).filter(
      (i) => i.list_id === input.list_id && (i.parent_id ?? null) === (input.parent_id ?? null) && !i.deleted_at
    );
    const temp: TaskItem = {
      id: tempId,
      ckey,
      list_id: input.list_id,
      parent_id: input.parent_id ?? null,
      depth: parent ? parent.depth + 1 : 0,
      sort_order: input.sort_order ?? siblings.reduce((max, i) => Math.max(max, i.sort_order), 0) + 1,
      text: input.text,
      note: input.note ?? null,
      progress: 0,
      assignees: input.assignees ?? [],
      assigned_by: (input.assignees || []).some((a) => a !== me) ? me : null,
      source: 'manual',
      link_type: input.link_type ?? null,
      link_ref: input.link_ref ?? null,
      link_label: input.link_label ?? null,
      due_date: input.due_date ?? null,
      waiting_on: null,
      follow_up_date: null,
      carry_count: 0,
      completed_at: null,
      completed_by: null,
      resolved_reason: null,
      created_by: me,
      created_at: new Date().toISOString(),
      deleted_at: null,
      rev: 0,
      linked_to: null,
      linked_list_id: null,
      linked_list_title: null,
      week_copy_list_id: null,
      node_kind: input.node_kind ?? 'task',
      qty: input.qty ?? null,
      cost: input.cost ?? null,
      vendor: input.vendor ?? null,
    };
    dispatch({ type: 'LOCAL_ITEMS', items: [temp] });

    const done = (async (): Promise<TaskItem | null> => {
      try {
        const parentId = input.parent_id && input.parent_id < 0 ? await realId(input.parent_id) : input.parent_id;
        const result = await taskApi.createItem({ ...input, parent_id: parentId ?? null } as any);
        const created = result.item || result.items[0];
        dispatch({ type: 'DROP_ITEM', id: tempId });
        mergeResult({ ...result, items: result.items.map((i) => (i.id === created.id ? { ...i, ckey } : i)) });
        // Edits typed into the row before the server answered.
        const patch = tempPatches.current.get(tempId);
        tempPatches.current.delete(tempId);
        if (patch && Object.keys(patch).length) {
          dispatch({ type: 'LOCAL_ITEMS', items: [{ ...created, ...patch, ckey }] });
          const followUp = await taskApi.updateItem(created.id, patch);
          mergeResult(followUp);
        }
        return created;
      } catch (error) {
        dispatch({ type: 'DROP_ITEM', id: tempId });
        fail(error, 'Could not add the item');
        return null;
      }
    })();
    tempIds.current.set(tempId, done.then((item) => item?.id ?? null));
    return { tempId, key: ckey, done };
  }, [me, realId, mergeResult, fail]);

  /** Local copy of what the server will do with a progress change. */
  const applyLocalProgress = (map: Map<number, TaskItem>, id: number, progress: number) => {
    const item = map.get(id);
    if (!item) return;
    const all = Array.from(map.values());
    const kids = descendantIds(all, id);
    const stamp = (i: TaskItem, value: number): TaskItem => ({
      ...i,
      progress: value,
      completed_at: value === 100 ? i.completed_at || new Date().toISOString() : null,
      completed_by: value === 100 ? i.completed_by || me : null,
    });
    if (kids.length && progress !== 0 && progress !== 100) return;
    kids.forEach((k) => map.set(k, stamp(map.get(k)!, progress)));
    map.set(id, stamp(item, progress));
    recomputeProgress(map, item.parent_id);
  };

  const updateItem = useCallback<TasksContextValue['updateItem']>(async (id, patch) => {
    const s = stateRef.current;
    const before = s.items;
    const working = new Map(before);
    const current = working.get(id);
    if (!current) return;
    const { progress, ...fields } = patch;
    working.set(id, { ...current, ...fields });
    if (progress !== undefined) {
      applyLocalProgress(working, id, progress);
      // A week-list link ticks its original (and everything under it) too,
      // as the server will.
      if (current.linked_to) applyLocalProgress(working, current.linked_to, progress);
    }
    const changed = Array.from(working.values()).filter((i) => before.get(i.id) !== i);
    dispatch({ type: 'LOCAL_ITEMS', items: changed });

    if (id < 0) {
      // Not created yet: fold into the create's follow-up write.
      tempPatches.current.set(id, { ...(tempPatches.current.get(id) || {}), ...patch });
      return;
    }

    const ids = changed.map((i) => i.id);
    hold(ids);
    try {
      mergeResult(await taskApi.updateItem(id, patch));
    } catch (error) {
      dispatch({ type: 'LOCAL_ITEMS', items: changed.map((i) => before.get(i.id)!).filter(Boolean) });
      fail(error, 'Could not save that change');
    } finally {
      release(ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergeResult, fail, me]);

  const deleteItem = useCallback(async (id: number) => {
    const s = stateRef.current;
    const current = s.items.get(id);
    if (!current) return;
    const ids = [id, ...descendantIds(Array.from(s.items.values()), id)];
    const stamp = new Date().toISOString();
    const working = new Map(s.items);
    ids.forEach((i) => working.set(i, { ...working.get(i)!, deleted_at: stamp }));
    recomputeProgress(working, current.parent_id);
    const changed = Array.from(working.values()).filter((i) => s.items.get(i.id) !== i);
    dispatch({ type: 'LOCAL_ITEMS', items: changed });

    const target = await realId(id);
    if (target === null) return;
    hold(ids);
    try {
      mergeResult(await taskApi.deleteItem(target));
    } catch (error) {
      dispatch({ type: 'LOCAL_ITEMS', items: changed.map((i) => s.items.get(i.id)!).filter(Boolean) });
      fail(error, 'Could not delete the item');
    } finally {
      release(ids);
    }
  }, [realId, mergeResult, fail]);

  const moveItems = useCallback<TasksContextValue['moveItems']>(async (listId, moves) => {
    if (!moves.length) return;
    const s = stateRef.current;
    const working = new Map(s.items);
    moves.forEach((m) => {
      const item = working.get(m.id);
      if (item) working.set(m.id, { ...item, parent_id: m.parent_id, depth: m.depth, sort_order: m.sort_order });
    });
    const parents = new Set<number | null>();
    moves.forEach((m) => {
      parents.add(m.parent_id);
      parents.add(s.items.get(m.id)?.parent_id ?? null);
    });
    parents.forEach((p) => recomputeProgress(working, p));
    const changed = Array.from(working.values()).filter((i) => s.items.get(i.id) !== i);
    dispatch({ type: 'LOCAL_ITEMS', items: changed });

    // Temp rows can't be moved server-side yet; their position lands with
    // the next structural change.
    const realMoves = moves.filter((m) => m.id > 0 && (m.parent_id === null || m.parent_id > 0));
    if (!realMoves.length) return;
    const ids = changed.map((i) => i.id);
    hold(ids);
    try {
      mergeResult(await taskApi.moveItems(listId, realMoves));
    } catch (error) {
      dispatch({ type: 'LOCAL_ITEMS', items: changed.map((i) => s.items.get(i.id)!).filter(Boolean) });
      fail(error, 'Could not move that item');
    } finally {
      release(ids);
    }
  }, [mergeResult, fail]);

  const uploadImage = useCallback<TasksContextValue['uploadImage']>(async (listId, payload) => {
    try {
      const { list } = await taskApi.uploadImage({ list_id: listId, ...payload });
      dispatch({ type: 'MERGE', lists: [list] });
    } catch (error) {
      fail(error, 'Upload failed');
      throw error;
    }
  }, [fail]);

  const deleteImage = useCallback(async (listId: number, url: string) => {
    try {
      const { list } = await taskApi.deleteImage(listId, url);
      dispatch({ type: 'MERGE', lists: [list] });
    } catch (error) {
      fail(error, 'Could not remove the photo');
    }
  }, [fail]);

  const markSeen = useCallback(async (ids: number[] | 'all') => {
    if (ids !== 'all' && !ids.length) return;
    const remaining =
      ids === 'all' ? [] : stateRef.current.notifications.filter((n) => !ids.includes(n.id));
    dispatch({ type: 'NOTIFICATIONS', notifications: remaining });
    try {
      const result = await taskApi.markSeen(ids === 'all' ? { all: true } : { ids });
      dispatch({ type: 'NOTIFICATIONS', notifications: result.notifications });
    } catch (error) {
      console.warn('markSeen failed', error);
    }
  }, []);

  // ── Week links & pins ──────────────────────────────────────────────────────

  const linkToWeek = useCallback(async (itemId: number) => {
    const target = await realId(itemId);
    if (target === null) return;
    try {
      mergeResult(await taskApi.linkToWeek(target));
    } catch (error) {
      fail(error, "Could not add it to the week list");
    }
  }, [realId, mergeResult, fail]);

  const unlinkFromWeek = useCallback(async (itemId: number) => {
    try {
      mergeResult(await taskApi.unlinkFromWeek(itemId));
    } catch (error) {
      fail(error, 'Could not take it off the week list');
    }
  }, [mergeResult, fail]);

  const setPinned = useCallback(async (listId: number, pinned: boolean) => {
    const before = stateRef.current.pinned;
    const next = pinned ? [...before.filter((id) => id !== listId), listId] : before.filter((id) => id !== listId);
    dispatch({ type: 'PINNED', pinned: next });
    try {
      const result = await taskApi.pinList(listId, pinned);
      dispatch({ type: 'PINNED', pinned: result.pinned_list_ids });
    } catch (error) {
      dispatch({ type: 'PINNED', pinned: before });
      fail(error, pinned ? 'Could not pin the list' : 'Could not unpin the list');
    }
  }, [fail]);

  // ── Derived views ──────────────────────────────────────────────────────────

  const lists = useMemo(
    () => Array.from(state.lists.values()).filter((l) => !l.deleted_at && !l.archived),
    [state.lists]
  );
  const items = useMemo(
    () => Array.from(state.items.values()).filter((i) => !i.deleted_at && state.lists.has(i.list_id) && !state.lists.get(i.list_id)!.deleted_at),
    [state.items, state.lists]
  );
  const byList = useMemo(() => {
    const map = new Map<number, TaskItem[]>();
    items.forEach((i) => map.set(i.list_id, [...(map.get(i.list_id) || []), i]));
    return map;
  }, [items]);
  const itemsForList = useCallback((listId: number) => byList.get(listId) || [], [byList]);
  const costs = useMemo(() => costTree(items), [items]);
  const canEditList = useCallback(
    (list: TaskList): boolean => {
      // A project's Lists card is worked through the project itself.
      if (list.kind === 'view') {
        const project = list.view_list_id ? state.lists.get(list.view_list_id) : undefined;
        return !!project && !project.deleted_at && !project.archived && project.kind === 'project' &&
          (project.owner_email === me || (state.members.get(project.id) || []).includes(me));
      }
      return list.is_team
        ? state.weeklyViewer
        : list.owner_email === me || (state.members.get(list.id) || []).includes(me) || (!!list.is_request && isAdmin);
    },
    [state.weeklyViewer, state.members, state.lists, me, isAdmin]
  );

  const value: TasksContextValue = {
    ready: state.ready,
    error: state.error,
    today: state.today,
    me,
    rev: state.rev,
    weeklyListId: state.weeklyListId,
    weeklyViewer: state.weeklyViewer,
    users: state.users,
    lists,
    items,
    itemsForList,
    costs,
    canEditList,
    members: state.members,
    notifications: state.notifications,
    refresh: bootstrap,
    createList,
    updateList,
    setMembers,
    deleteList,
    createProject,
    showOnLists,
    createRequest,
    createItem,
    updateItem,
    deleteItem,
    moveItems,
    uploadImage,
    deleteImage,
    markSeen,
    pinned: state.pinned,
    setPinned,
    linkToWeek,
    unlinkFromWeek,
    composer,
    openComposer: (preset = {}) => setComposer(preset),
    closeComposer: () => setComposer(null),
    listDialog,
    openListDialog: (listId, itemId = null) => setListDialog({ listId, itemId }),
    closeListDialog: () => setListDialog(null),
    lastError,
    clearError: () => setLastError(null),
  };

  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>;
};

export function useTasks(): TasksContextValue {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error('useTasks must be used within TasksProvider');
  return ctx;
}

/** For components that render outside the provider (e.g. before login). */
export function useOptionalTasks(): TasksContextValue | undefined {
  return useContext(TasksContext);
}
